import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "react-toastify";

import ENV from "config/Env";
import { typedQueryClient, useTypedQuery } from "hooks";
import { User } from "models";
import { UsersKeys } from "modules/Users/queryKeys";
import { CreateUser } from "modules/Users/types";
import ApiInstance from "util/Api";

export const useUsersSearch = () => {
  const filters = typedQueryClient.getQueryData(UsersKeys.filters);

  const query = useTypedQuery({
    queryKey: UsersKeys.users,
    queryFn: async () => {
      const response = await ApiInstance.post("/user/search", filters, ENV.systemBaseUrl);
      return { nb_items: response.data.nb_items || 0, nb_pages: response.data.nb_pages || 0, users: response.data.users ? (response.data.users as User[]) : [] };
    }
  });

  useEffect(() => {
    query.refetch();
  }, [filters]);

  return query;
};

export const useUsersCreate = () =>
  useMutation({
    mutationFn: async (payload: CreateUser) => {
      const response = await ApiInstance.post("/user/create", payload, ENV.systemBaseUrl);
      return response.data.user_id as number;
    },
    onSuccess: (userId, payload) => {
      const previousUsers = typedQueryClient.getQueryData(UsersKeys.users);
      if (!previousUsers) return;

      typedQueryClient.setQueryData(UsersKeys.users, {
        ...previousUsers,
        nb_items: previousUsers.nb_items + 1,
        users: [
          { ...payload.user, id: userId, created_on: new Date().toISOString(), updated_on: new Date().toISOString(), session_expire_at: "", acl: [] },
          ...previousUsers.users
        ]
      });
      return { previousUsers };
    },
    onError: e => toast.error(e.message)
  });

export const useUserDelete = () =>
  useMutation({
    mutationFn: async (id: number) => {
      const response = await ApiInstance.post("/user/delete", { id }, ENV.systemBaseUrl);
      return response.data as User;
    },
    onMutate: (id: number) => {
      const previousUsers = typedQueryClient.getQueryData(UsersKeys.users);
      if (!previousUsers) return;

      const userIdx = previousUsers.users.findIndex(user => user.id === id);
      if (userIdx === -1) return;

      typedQueryClient.setQueryData(UsersKeys.users, {
        ...previousUsers,
        nb_items: previousUsers.nb_items - 1,
        users: previousUsers.users.filter(user => user.id !== id)
      });
      return { previousUsers };
    },
    onError: (e, _, context) => {
      toast.error(e.message);
      if (context?.previousUsers) typedQueryClient.setQueryData(UsersKeys.users, context.previousUsers);
    }
  });

export const useUserUpdate = () =>
  useMutation({
    mutationFn: async (payload: { user: Partial<User> }) => {
      const response = await ApiInstance.post("/user/update", payload, ENV.systemBaseUrl);
      return response.data as User;
    },
    onMutate: (payload: { user: Partial<User> }) => {
      const previousUsers = typedQueryClient.getQueryData(UsersKeys.users);
      if (!previousUsers) return;

      const userIdx = previousUsers.users.findIndex(user => user.id === payload.user.id);
      if (userIdx === -1) return;

      typedQueryClient.setQueryData(UsersKeys.users, {
        ...previousUsers,
        users: previousUsers.users.with(userIdx, { ...previousUsers.users[userIdx], ...payload.user })
      });

      return { previousUsers };
    },
    onError: (e, _, context) => {
      toast.error(e.message);
      if (context?.previousUsers) typedQueryClient.setQueryData(UsersKeys.users, context.previousUsers);
    }
  });
